/**
 * Multi-image attachments without a schema change.
 *
 * The chat `Message` schema stores a single `attachmentUrl` + `attachmentType`.
 * To send several images in one message we keep the first image in the schema
 * fields (so thread previews and legacy single-image rendering keep working) and
 * stash the additional S3 attachment URLs in the message body behind a
 * machine-readable marker, mirroring the existing reply/GIF body markers.
 */
const IMAGES_MARKER = /^<!--ascend-images:(.+?)-->\n?/

/** Encode extra image URLs (beyond the primary) plus an optional caption. */
export function encodeImagesMessageBody(extraUrls: string[], caption?: string): string {
  const urls = extraUrls.map((u) => u.trim()).filter(Boolean)
  const cap = caption?.trim() ?? ''
  if (urls.length === 0) return cap
  const marker = `<!--ascend-images:${JSON.stringify(urls)}-->`
  return cap ? `${marker}\n${cap}` : marker
}

/** Parse a body that may contain the multi-image marker. */
export function parseImagesMessageBody(body: string): {
  extraUrls: string[]
  visibleText: string
} {
  const trimmed = body?.trim() ?? ''
  if (!trimmed) return { extraUrls: [], visibleText: '' }

  const match = trimmed.match(IMAGES_MARKER)
  if (!match) return { extraUrls: [], visibleText: trimmed }

  try {
    const parsed = JSON.parse(match[1]) as unknown
    const extraUrls = Array.isArray(parsed)
      ? parsed.filter((u): u is string => typeof u === 'string' && Boolean(u.trim()))
      : []
    return { extraUrls, visibleText: trimmed.slice(match[0].length).trim() }
  } catch {
    return { extraUrls: [], visibleText: trimmed }
  }
}

/** Strip the multi-image marker from a thread-list preview string. */
export function stripImagesMarker(body: string): string {
  return parseImagesMessageBody(body).visibleText
}
